import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const FIELDS = ['Name', 'phone_c', 'serviceArea_c', 'skills_c', 'availability_c', 'status_c', 'onboardingStatus_c', 'aadhaarVerificationStatus_c', 'licenseVerificationStatus_c', 'bankVerificationStatus_c'];
export const route = { path: '/workers', layout: 'owner', access: "profile === 'Agent_c'" };
export const nav = { icon: 'Users', label: 'Workers', section: 'Operations', profiles: ['Agent_c'], order: 20 };

export default function Workers() {
  const [search, setSearch] = useState('');
  const [availability, setAvailability] = useState('All');
  const { data, loading, error, run } = useFetch(() => {
    const { contains, eq } = window.ApperSDK.CoreSDK;
    let query = sdk.table('workers_c').select(FIELDS);
    if (search.trim()) query = query.where(contains('Name', search.trim()));
    if (availability !== 'All') query = query.where(eq('availability_c', availability));
    return query.orderByDesc('CreatedOn').fetch().then((r) => {
      if (!r.success) throw new Error(r.message || 'Could not load workers');
      return r.data;
    });
  }, [search, availability]);
  const workers = data ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Field operations</p>
          <h1 className="mt-2 font-heading text-3xl font-bold">Worker roster</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Review service skills, operating area and availability before coordinating a booking.</p>
        </div>
        <Button asChild><Link to="/workers/new">＋ Onboard worker</Link></Button>
      </header>
      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Rostered" value={workers.length} />
        <Metric label="Available" value={workers.filter((w) => w.availability_c === 'Available').length} />
        <Metric label="On a job" value={workers.filter((w) => w.availability_c === 'On job').length} />
      </section>
      <section className="rounded-2xl border border-border bg-card p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search worker name…" aria-label="Search workers" />
          <select value={availability} onChange={(e) => setAvailability(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" aria-label="Filter availability">
            <option>All</option><option>Available</option><option>On job</option><option>Off duty</option>
          </select>
          <Button variant="outline" onClick={run} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button>
        </div>
        {error && <p className="mt-4 text-sm text-destructive">Could not load roster. <button className="underline" onClick={run}>Retry</button></p>}
        <div className="mt-5 space-y-3">
          {loading && !data ? [1,2,3].map((n) => <div key={n} className="h-20 animate-pulse rounded-xl bg-muted" />) :
          workers.length ? workers.map((worker) => <article key={worker.Id} className="grid gap-3 rounded-xl border border-border p-4 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-center">
            <div><p className="font-semibold">{worker.Name}</p><p className="mt-1 text-xs text-muted-foreground">{worker.phone_c || 'Phone not added'} · {worker.serviceArea_c || 'Area not set'}</p></div>
            <div><p className="text-sm">{worker.skills_c || 'Skills not recorded'}</p><p className="mt-1 text-xs text-muted-foreground">Service skills</p></div>
            <span className={`inline-flex w-fit rounded-full px-3 py-1.5 text-xs font-bold ${worker.availability_c === 'Available' ? 'bg-success-muted text-success' : worker.availability_c === 'On job' ? 'bg-warning-muted text-warning-foreground' : 'bg-muted text-muted-foreground'}`}>{worker.availability_c || 'Unknown'}</span>
          </article>) : <div className="rounded-xl border border-dashed border-border p-8 text-center"><p className="font-semibold">No workers match</p><p className="mt-1 text-sm text-muted-foreground">Change the search or availability filter.</p></div>}
        </div>
        <p className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">{workers.length} worker record{workers.length === 1 ? '' : 's'} in this view</p>
      </section>
      <p className="text-xs leading-5 text-muted-foreground">Worker accounts and identity verification are managed separately by the Owner. This screen is for Agent-side roster visibility.</p>
    </div>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-xl border border-border bg-card p-4"><p className="text-sm text-muted-foreground">{label}</p><p className="mt-2 font-heading text-3xl font-bold">{value}</p></div>;
}